import * as Dialog from "@radix-ui/react-dialog";
import { AlertTriangle, Info } from "lucide-react";
import { getContrastTextColor } from "../../lib/color-utils";

type ConfirmType = "danger" | "warning" | "info";

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: ConfirmType;
  primaryColor?: string;
}

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "danger",
  primaryColor = "#0F52BA", // Default to brand sapphire
}: ConfirmModalProps) {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case "danger":
        return {
          icon: <AlertTriangle className="text-slate-900" size={18} />,
          iconBg: "bg-slate-50",
          confirmBtn:
            "bg-slate-900 hover:bg-black focus-visible:ring-slate-900",
        };
      case "warning":
        return {
          icon: <AlertTriangle className="text-amber-600" size={18} />,
          iconBg: "bg-amber-50/50",
          confirmBtn:
            "bg-slate-900 hover:bg-black focus-visible:ring-slate-900",
        };
      case "info":
      default:
        return {
          icon: <Info className="text-slate-900" size={18} />,
          iconBg: "bg-slate-50",
          confirmBtn:
            "bg-slate-900 hover:bg-black focus-visible:ring-slate-900",
          customBg: true,
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-[2px] transition-opacity" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-[100] translate-x-[-50%] translate-y-[-50%] bg-white rounded-xl shadow-xl border border-slate-100 w-[calc(100%-2rem)] sm:w-full sm:max-w-md transform transition-all outline-none animate-in fade-in zoom-in-95 duration-200 overflow-hidden">
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div
                className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${styles.iconBg} border border-slate-100`}
              >
                {styles.icon}
              </div>
              <div className="flex-1 min-w-0">
                <Dialog.Title className="text-base font-bold text-slate-900 tracking-tight leading-tight">
                  {title}
                </Dialog.Title>
                <p className="mt-2 text-slate-500 text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-relaxed">
                  Notice
                </p>
                <Dialog.Description className="mt-1 text-slate-600 text-sm leading-relaxed">
                  {message}
                </Dialog.Description>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end bg-slate-50/50 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-5 py-2 rounded-lg font-bold text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all focus-visible:outline-none focus:ring-2 focus:ring-slate-200 active:scale-[0.98]"
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={() => {
                onConfirm();
                onClose();
              }}
              className={`w-full sm:w-auto px-6 py-2 rounded-lg font-bold text-white transition-all focus-visible:outline-none focus-visible:ring-2 active:scale-[0.98] ${
                styles.customBg ? "" : styles.confirmBtn
              }`}
              style={
                styles.customBg
                  ? {
                      backgroundColor: primaryColor,
                      color: getContrastTextColor(primaryColor),
                    }
                  : undefined
              }
            >
              {confirmText}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
